import { Images } from '../../../../../shared/app-constants';
import {
  br2nl,
  getMergeTagsMappingWithValues,
} from '../../../../sequence/shared/modals/email-modal/components/sidebar/tabs/utils/get-merge-tag-mapping-with-values';
import { replaceAttributesWithValues } from '../../../../sequence/shared/modals/email-modal/components/sidebar/tabs/utils/replace-attributes-with-value';
import { EnrichmentTemplateCategory } from '../../../enums/prospect';
import {
  AiColEnrichmentModalSteps,
  SupportedAiModelsOutput,
  SupportedAiModelsOutputColumnType,
  SupportedAiModelsServiceProvider,
} from '../enums';
import { getAiModelsProps, getTemplateByIdProps } from '../../../types';
import { convertHtmlToText } from '../../../../sequence/shared/modals/email-modal/components/sidebar/tabs/utils/get-parsed-content-for-text-only-email';
import { stripChipTagsFromHtml } from '../../../../../shared/editor/helpers';

export const templateEnrichment = [
  {
    id: 'use-template',
    title: 'Use Template to Enrich Prospect',
    description:
      'Save time with ready-made enrichment templates, built by experts.',
    image: Images.UseTemplateToEnrich,
    navigationLinkText: 'Explore Templates',
  },
  // {
  //   id: 'own-prompt',
  //   title: 'Enrich with Your Own Prompt',
  //   description:
  //     'Enter your instructions manually to quickly enrich data for prospects with AI.',
  //   image: Images.OwnPrompt,
  // },
  {
    id: 'existing-prospect',
    title: 'Write Your Own Prompt',
    description: 'Customize enrichment by adding your own instructions.',
    image: Images.WriteOwnPrompt,
    navigationLinkText: 'Start Building',
  },
];

export const templateTypes = [
  {
    key: '0',
    label: `All`,
    value: null,
  },
  {
    key: EnrichmentTemplateCategory.ColdEmailAssistant,
    label: `Cold Email Assistant`,
    value: null,
  },
  {
    key: EnrichmentTemplateCategory.MarketResearcher,
    label: `Market Researcher`,
    value: null,
  },
  {
    key: EnrichmentTemplateCategory.CompanyDataLookup,
    label: `Company Data lookup`,
    value: null,
  },
  {
    key: EnrichmentTemplateCategory.DataCleanup,
    label: `Data Cleanup`,
    value: null,
  },
  {
    key: EnrichmentTemplateCategory.PeopleDataFinder,
    label: `People Data Finder`,
    value: null,
  },
  {
    key: EnrichmentTemplateCategory.DataClassifier,
    label: `Data Classifier`,
    value: null,
  },
  {
    key: EnrichmentTemplateCategory.SocialMedia,
    label: `Social Media`,
    value: null,
  },
  {
    key: EnrichmentTemplateCategory.UserDefined,
    label: `Saved Templates`,
    value: null,
  },
];

export const AiOptions = [
  {
    key: '0',
    label: `chatgpt`,
    value: null,
  },
  {
    key: '11',
    label: `gemini`,
    value: 'newly active',
  },
  {
    key: '12',
    label: `claude`,
    value: 'paused',
  },
  {
    key: '13',
    label: `perplexity`,
    value: 'draft',
  },
];

export const generateEmailPreviewMergeTag = ({
  content,
  mergeTags,
  attributes,
}: {
  content: string;
  mergeTags: any[];
  attributes?: Record<string, any>[];
}): { content: string; error: boolean } => {
  let parsedContent = content;
  let hasError = false;

  if (!attributes || attributes.length === 0) {
    return { content: parsedContent, error: true };
  }

  // Find all merge tags actually used in content
  const tagsInContent = mergeTags.filter((tag) =>
    content.toLowerCase().includes(`{{${tag.label.toLowerCase()}}}`),
  );

  // Map attributes, only flagging error for tags used in content
  const safeAttributes = attributes.map((attr) => {
    const tagLabel = attr?.fieldRef?.label ?? '';
    const isTagInContent = tagsInContent.some(
      (tag) => tag?.label?.toLowerCase() === tagLabel?.toLowerCase(),
    );

    // Default value handling
    let value: string | undefined;

    if (
      tagLabel.toLowerCase() === 'email' &&
      Array.isArray(attr?.attributeValue)
    ) {
      // Pick primary email if available, else first one
      const emailObj =
        attr.attributeValue.find((e: any) => e.isPrimary) ??
        attr.attributeValue[0];
      value = emailObj?.email;
    } else if (attr?.attributeValue != null) {
      value = String(attr.attributeValue);
    }

    if (isTagInContent && (!value || value.trim() === '')) {
      hasError = true;
    }

    return {
      ...attr,
      attributeValue: value ?? `{{${tagLabel}}}`,
    };
  });

  const {
    mergeTagsMappingForRegex,
    mergeTagsMappingWithValues,
  } = getMergeTagsMappingWithValues(mergeTags, safeAttributes);

  parsedContent = replaceAttributesWithValues(
    parsedContent,
    mergeTagsMappingForRegex,
    mergeTagsMappingWithValues,
    'body',
  );

  return { content: parsedContent, error: hasError };
};

export const extractTags = (
  innerText: string,
): { validTags: string[]; malformedTags: string[] } => {
  const validTags: string[] = [];
  const malformedTags: string[] = [];
  let currentTag = '';
  let openBraceCount = 0;

  Array.from(innerText)?.forEach((char) => {
    if (char === '{') {
      currentTag += char;
      openBraceCount += 1;
    } else if (char === '}') {
      currentTag += char;
      openBraceCount -= 1;

      if (openBraceCount === 0) {
        if (currentTag?.startsWith('{{') && currentTag?.endsWith('}}')) {
          validTags?.push(currentTag);
        } else {
          malformedTags?.push(currentTag);
        }
        currentTag = '';
      } else if (openBraceCount < 0) {
        malformedTags?.push(currentTag);
        currentTag = '';
        openBraceCount = 0;
      }
    } else if (openBraceCount > 0) {
      currentTag += char;
    }
  });

  if (openBraceCount > 0) {
    malformedTags?.push(currentTag);
  }
  return { validTags, malformedTags };
};

const decodeHtmlEntities = (text: string): string => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

export const validateMergeTags = (
  text: string,
  validTagsSet: Set<string>,
): { errors: string[]; validTags: string[] } => {
  const validationErrors: string[] = [];
  const validMatchedTags: string[] = [];

  const converrtHTMLToText = convertHtmlToText(stripChipTagsFromHtml(text));

  if (!converrtHTMLToText || converrtHTMLToText?.trim() === '') {
    validationErrors?.push('');
    return { errors: validationErrors, validTags: validMatchedTags };
  }
  const { validTags, malformedTags } = extractTags(converrtHTMLToText);

  if (malformedTags.length > 0) {
    validationErrors?.push(`${malformedTags?.join(', ')}`);
    return { errors: validationErrors, validTags: validMatchedTags };
  }

  validTags.forEach((tag) => {
    const innerText = tag?.startsWith('{{')
      ? tag?.slice(2, -2)
      : tag?.slice(1, -1);
    const normalizedText = decodeHtmlEntities(innerText?.trim()?.toLowerCase());
    const OnlyMerge = innerText?.split('|')[0];

    if (normalizedText !== decodeHtmlEntities(innerText?.toLowerCase())) {
      validationErrors.push(`Tag contains invalid spaces: ${tag}`);
    } else if (
      !Array.from(validTagsSet).some(
        (validTag) =>
          decodeHtmlEntities(validTag?.trim()?.toLowerCase()) ===
          decodeHtmlEntities(OnlyMerge?.trim()?.toLowerCase()),
      )
    ) {
      validationErrors.push(`Invalid tag: ${tag}`);
    } else {
      validMatchedTags.push(tag?.replace(/^\{\{|\}\}$/g, '')?.trim());
    }
  });
  return { errors: validationErrors, validTags: validMatchedTags };
};

export const validatePrompt = (
  prompt: string,
  validationErrors: string[],
  Regex: RegExp,
) => {
  // const cleanText = prompt?.replace(/<[^>]*>/g, '').trim() ?? '';
  const cleanText = br2nl(prompt);

  if (cleanText.length > 5000) {
    return { errors: true, errorMessages: 'Character limit exceeded' };
  }
  if (cleanText.length >= 1 && cleanText.length < 20) {
    return { errors: true, errorMessages: 'Minimum 20 characters required' };
  }
  if (cleanText.length >= 1 && validationErrors?.length > 0) {
    return { errors: true, errorMessages: 'Invalid Merge Tag' };
  }
  if (cleanText.length >= 1 && !Regex.test(cleanText)) {
    return {
      errors: true,
      errorMessages: 'At least one merge tag is required',
    };
  }

  return { errors: false, errorMessages: '' };
};

export const AI_ICONS = {
  [SupportedAiModelsServiceProvider.Anthropic]: Images.Anthropic,
  [SupportedAiModelsServiceProvider.OpenAI]: Images.OpenAI,
  [SupportedAiModelsServiceProvider.Google]: Images.Gemini,
  [SupportedAiModelsServiceProvider.Perplexity]: Images.Perplexity,
  [SupportedAiModelsServiceProvider.XAI]: Images.Grok,
};

export const OutputTypeLabels: Record<
  SupportedAiModelsOutputColumnType,
  string
> = {
  [SupportedAiModelsOutputColumnType.PlainText]: 'Plain Text',
  [SupportedAiModelsOutputColumnType.LongText]: 'Long Text',
  [SupportedAiModelsOutputColumnType.Number]: 'Number',
};

export const AI_MODEL_LABELS: Record<string, string> = {
  'gpt-5': 'GPT 5',
  'gpt-5-mini': 'GPT 5 Mini',
  'gpt-5-nano': 'GPT 5 Nano',
  'gpt-4.1': 'GPT 4.1',
  'gpt-4.1-mini': 'GPT 4.1 Mini',
  'claude-sonnet-4': 'Claude Sonnet 4',
  'claude-sonnet-3.7': 'Claude Sonnet 3.7',
  'perplexity-sonar': 'Perplexity Sonar',
  'perplexity-sonar-pro': 'Perplexity Sonar Pro',
  'gemini-2.5-flash': 'Gemini 2.5 Flash',
  'gemini-2.5-pro': 'Gemini 2.5 Pro',
  'grok-4': 'Grok 4',
};

export const formatOptions = (
  getTemplateById: getTemplateByIdProps,
  currentStep: AiColEnrichmentModalSteps,
  getAiModelList: getAiModelsProps[],
  enrichPromptData: any,
) => {
  // Define the format configuration mapping
  const formatConfig: Record<
    string,
    { label: string; icon: any; text: string }
  > = {
    [SupportedAiModelsOutput.Default]: {
      label: 'Default',
      icon: Images.Defaultvalue,
      text: 'Standard response format with natural formatting and structure',
    },
    [SupportedAiModelsOutput.CommaSeparatedValues]: {
      label: 'Comma Separated values',
      icon: Images.Comma,
      text: 'Sarah Johnson, VP Sales, TechCorp',
    },
    [SupportedAiModelsOutput.OneWord]: {
      label: 'One Word',
      icon: Images.LowerCase,
      text: 'Interested',
    },
    [SupportedAiModelsOutput.Email]: {
      label: 'Email',
      icon: Images.Mail,
      text: 'A professional email with proper structure and greeting',
    },
    [SupportedAiModelsOutput.BulletPoints]: {
      label: 'Bullet Points',
      icon: Images.Bullet,
      text: 'Structured bullet point list with HTML formatting',
    },
    [SupportedAiModelsOutput.NumberList]: {
      label: 'Number List',
      icon: Images.NumberList,
      text: `1. Import your prospect list
       2. Choose proven templates 
       3. Schedule smart sequences`,
    },
    [SupportedAiModelsOutput.HtmlSnippet]: {
      label: 'HTML SNIPPET',
      icon: Images.HTML,
      text: 'Raw HTML code that can be used directly into emails',
    },
    [SupportedAiModelsOutput.LinkOnly]: {
      label: 'Link Only',
      icon: Images.LinkChain,
      text: 'https://www.saleshandy.com/schedule-demo',
    },
    [SupportedAiModelsOutput.SingleSentence]: {
      label: 'Single Sentence',
      icon: Images.SingleSentence,
      text:
        'Saleshandy helps sales team book 40% more meetings through personalised email automation that actually feels human',
    },
    [SupportedAiModelsOutput.ShortParagraph]: {
      label: 'Short Paragraph',
      icon: Images.ShortParagraph,
      text:
        'Concise Paragraph format that typically consists 2-4 sentences with key information',
    },
    [SupportedAiModelsOutput.LongParagraph]: {
      label: 'Long Paragraph',
      icon: Images.LongParagraph,
      text:
        'Detailed paragraph format comprehensive explanation with continuous text',
    },
    [SupportedAiModelsOutput.Custom]: {
      label: 'Custom',
      icon: Images.Pencil,
      text:
        'User defined format. Specify your own output structure and style requirements',
    },
  };

  // Get available output formats from API response
  let availableFormats = [];
  if (currentStep === AiColEnrichmentModalSteps.TEMPLATE_DETAIL) {
    // Get available output formats from API response
    availableFormats = getTemplateById?.suggestedAiModel?.outputFormat || [];
  } else {
    // Derive available formats from the selected AI model only
    const selectedModel = getAiModelList?.find(
      (model) => String(model?.id) === String(enrichPromptData?.aiModelId),
    );
    availableFormats = selectedModel?.outputFormat || [];
  }

  // Filter and map the formats based on API response using flatMap
  return availableFormats.flatMap((format) =>
    formatConfig[format]
      ? [
          {
            key: format,
            label: formatConfig[format].label,
            icon: formatConfig[format].icon,
            value: null,
            text: formatConfig[format].text,
          },
        ]
      : [],
  );
};
